import { useEffect, useRef } from 'react';

import posthog from 'posthog-js';

import { resolveActivity } from '../analytics/activity';
import { resolveWorkstream, Workstream } from '../analytics/workstream';
import { resolveAppBranch } from '../appBranch';
import { isDevboxAuth, useAppAuth } from '../contexts/AppAuthContext';
import { paths } from '../routes/paths';

// The workstream of the very first event. The path table alone mis-tags
// the landing event on a host-keyed hostname: there the HOST forces one
// tree for every path (appBranch.ts), while the raw path at module-eval
// time may still read '/', '/search', anything — so the host decision
// short-circuits before the path table is consulted.
//
// Both host-keyed branches need this. On portfolio.bidequity.ai the landing
// path is '/', which the path table reads as the internal app's home and
// would tag 'admin-platform' — the cockpit's own bucket is what the PortCo
// actually lands in, and resolving through paths.cockpit keeps this tied to
// the same table entry every other /cockpit/* pathname already resolves by.
export function initialWorkstream(hostname: string, pathname: string): Workstream {
  const branch = resolveAppBranch(hostname, pathname);
  if (branch === 'seller') return 'vdr';
  if (branch === 'cockpit') return resolveWorkstream(paths.cockpit);
  return resolveWorkstream(pathname);
}

// Which PostHog project this build reports to, supplied at build time so that
// moving projects is a config change rather than a code change. The key was a
// literal here until a project move left the deployed bundle reporting to the
// old project while every other PostHog credential had already been repointed.
//
// There is deliberately no fallback literal. A stale default is the failure
// this replaces: it sends real user events to whichever project was current
// when the line was written, and nothing surfaces the mistake. Reporting
// nowhere is recoverable and visible; reporting to the wrong project quietly
// corrupts another project's data.
//
// This is PostHog's public write-only ingest key (`phc_`), which ships in the
// bundle to every browser. It is configuration, not a credential — it lives in
// a secret store for rotation, not confidentiality.
//
// Read inside initPostHog rather than at module scope. The built bundle is
// identical either way, because CRA inlines process.env.REACT_APP_* wherever
// the expression appears, but a module-scope constant is captured at import
// time and so cannot be varied per test without re-requiring this module —
// which hands the component a second copy of React and breaks its hooks.
const posthogKey = () => process.env.REACT_APP_POSTHOG_KEY;
const posthogHost = () => process.env.REACT_APP_POSTHOG_HOST || 'https://eu.posthog.com';

function isTestEnv(): boolean {
  return (
    process.env.NODE_ENV === 'test' ||
    process.env.REACT_APP_ENV === 'test' ||
    typeof (window as any).__TEST__ !== 'undefined'
  );
}

let posthogInitialized = false;

/**
 * Whether `posthog.init` has actually run in this bundle.
 *
 * Read by the feature-flag layer to decide whether PostHog is an authority on
 * this build at all. It is not on every build: `initPostHog` refuses without
 * `REACT_APP_POSTHOG_KEY`, and it is skipped outright under test. A flag read
 * against a client that was never initialised answers `undefined` forever,
 * which is indistinguishable from "flag is off" — so a rollout gate that
 * could not tell the two apart would turn every gated feature off in local
 * development and in any environment missing the key. See
 * `useFeatureToggle`, which uses this to fall back to the `cortex_config`
 * value alone.
 */
export function isPostHogInitialized(): boolean {
  return posthogInitialized;
}

// Called from App.tsx above the internal/cockpit branch split, because only
// the internal branch renders <PostHogProvider />, and this is the single
// caller of posthog.init in the app. Without it every posthog.register and
// posthog.capture under /cockpit/* lands on an uninitialised client and is
// discarded, taking the whole buy-and-build tree out of the workstream
// dashboard.
export function initPostHog() {
  if (posthogInitialized || isTestEnv()) return;
  const key = posthogKey();
  if (!key) {
    // posthog.init on an empty key does not throw — it builds a client that
    // drops every event — so an unset variable would otherwise look exactly
    // like working analytics until someone noticed the dashboards were flat.
    // eslint-disable-next-line no-console
    console.error('REACT_APP_POSTHOG_KEY is not set: product analytics are disabled for this build.');
    return;
  }
  posthog.init(key, {
    api_host: posthogHost(),
    capture_pageview: true,
    capture_pageleave: true,
    // A Clerk invitation link carries a sign-up credential in the
    // __clerk_ticket query parameter. posthog-js masks the listed parameters
    // in $current_url, the persisted initial URL and the session replay URLs,
    // but only while mask_personal_data_properties is true. That flag also
    // masks ad click ids such as gclid. No code in this app reads them.
    mask_personal_data_properties: true,
    custom_personal_data_properties: ['__clerk_ticket'],
    session_recording: {
      maskAllInputs: false,
      maskInputFn: (text, element) => {
        if (element?.getAttribute('type') === 'password') return '*'.repeat(text.length);
        // Opt-in masking for inputs that carry sensitive free text (support
        // form, deal notes) but are not password fields.
        if (element?.hasAttribute('data-ph-mask-input')) return '*'.repeat(text.length);
        return text;
      },
    },
    autocapture: true,
    // The workstream super property has to exist before the first event,
    // not after the first render. posthog-js calls `loaded` synchronously
    // inside init and defers the initial $pageview to a timeout, while the
    // route-derived registrations in RootLayot and CockpitSectionTracker are
    // layout effects that run later still. Registering here is what keeps the
    // initial $pageview and `logged_in` from carrying no workstream at all,
    // or the bucket persisted from the previous route.
    loaded: () => {
      const { activity_id, activity_candidates, activity_label } = resolveActivity(
        window.location.pathname,
        window.location.search
      );
      posthog.register({
        workstream: initialWorkstream(window.location.hostname, window.location.pathname),
        activity_id,
        activity_candidates,
        activity_label,
      });
    },
  });
  posthogInitialized = true;
}

export default function PostHogProvider() {
  const { userId, userEmail } = useAppAuth();
  const identifiedRef = useRef<string | null>(null);

  useEffect(() => {
    if (isTestEnv()) return;
    initPostHog();
  }, []);

  useEffect(() => {
    // A devbox session is a synthetic local identity.
    if (!userId || isDevboxAuth() || isTestEnv() || !posthogInitialized) return;

    posthog.opt_in_capturing();

    if (identifiedRef.current !== userId) {
      posthog.identify(userId, {
        email: userEmail,
        name: userEmail,
      });
      posthog.capture('logged_in');
      identifiedRef.current = userId;
    }
  }, [userId, userEmail]);

  return null;
}
